<template>
  <v-flex
    v-if="!loading"
    xs8
    offset-xs2
    align-self-center
    justify-center
  >
  <div>
    <v-container fill-height fluid>
    <v-row>
      <v-flex xs3 class="mr-2">
        <Search
          v-model="filters.search"
          :label="$t('SEARCH')"
          prepend-icon="search"
          :delay="500"
        />
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('create zones')"
        @click="showWarehouseModal(0)"
        color="primary"
        dark
        class="mb-2"
      >{{ $t("NEW") }}
      </v-btn>

    </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      class="elevation-1"
    >
      <template v-slot:item.name="{ item }">
        <router-link :to="{ name: routeName.WAREHOUSE_SHOW, params: { id: item.id } }">
            {{ item.name }}
        </router-link>
      </template>

      <template v-slot:item.is_default="{ item }">
        <v-icon v-if="item.is_default">done</v-icon>
        <v-icon v-else>close</v-icon>
      </template>

      <template v-slot:item.updated_at="{ item }">
            {{ item.updated_at | timeFormat }}
      </template>

      <template v-slot:item.actions="{ item }">
            <v-layout row wrap>
              <v-flex class="text-no-wrap">
                <v-btn
                  icon
                  color="blue"
                  v-if="can('edit zones') && !parseInt(item.propagandist_id)"
                  @click="showWarehouseModal(item.id)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>

                <v-btn
                  v-if="can('delete zones') && !parseInt(item.propagandist_id)"
                  icon
                  color="red"
                  @click="deleteZone(item.id)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>

                <v-btn
                  icon
                  color="blue"
                  v-if="can('edit zones')"
                  @click="download(item.id)"
                >
                  <v-icon>cloud_download</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
      </template>
    </v-data-table>
    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchWarehouses"
    />
  </div>
  </v-flex>
  <Loader v-else/>
</template>
<script>

  import { mapActions } from 'vuex';

  import { warehousesService } from '../../utils/services/warehouses.service';
  import Search from '../../components/shared/Inputs/Search';
  import Loader from '../../components/shared/Loader';
  import WarehouseModal from '../../components/modals/Warehouses/WarehouseModal';
  import Paginator from '../../components/shared/Paginator';

  export default {
    name: 'WarehouseList',
    components: { Search, Loader, Paginator },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['name'],
        },
        headers: [
          {
            text: this.$t('fields.id'),
            align: 'left',
            value: 'id',
          },
          { text: this.$t('fields.name'), value: 'name' },
          { text: this.$t('fields.default'), value: 'is_default' },
          { text: this.$t('fields.updated_at'), value: 'updated_at' },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'right' },
        ],
        filters: {
          search: null,
        },
      };
    },
    computed: {
      requestFilters() {
        const filters = {
          search: this.filters.search,
        };
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),
      async fetchWarehouses(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return warehousesService.index({
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      async showWarehouseModal(id) {
        this.showModal({
          component: WarehouseModal,
            data: {
              stored: {
                id,
              },
          },
          options: {
            title: this.$t('WAREHOUSE'),
            onSave: (
              form,
              error,
            ) => warehousesService.save(form)
            .then(() => {
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
            })
            .catch(error => {
              this.handleError(error, this.errors);
            })
            .finally(() => {
              this.fetchWarehouses(this.pagination.page);
            }),
          },
        });
      },
      async deleteZone(id) {
          this.showConfirm({
            onConfirm: () => {
              this.loading = true;
              return warehousesService.remove(id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchWarehouses(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.errors);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },

      download(id) {
        this.loading = true;

        return warehousesService.product(id, { export: true })
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'books.csv');
            document.body.appendChild(link);
            link.click();
          }).catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
            this.hideModal({});
          });
      },
    },
    async created() {
      await this.fetchWarehouses(this.pagination.page);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchWarehouses(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchWarehouses(1);
          }
        },
      },

    },
  };
</script>
